/**
 * Native Shoes — Global hover-prefetch
 * -----------------------------------------------------------------
 * Author:   Anton Siedlecki
 * Date:     2026-10-03
 *
 * instant.page-style behavior (hover a link → prefetch its HTML so the
 * click feels instant). Global across the storefront, EXCEPT anything
 * that isn't a plain navigation — explicitly excluded below rather than
 * relying on a narrow selector, since the real risk isn't "wasted
 * request," it's accidentally firing a GET that mutates state before
 * the shopper actually clicks:
 *
 *   - /cart/add, /cart/change, /cart/update, /cart/clear — Shopify's
 *     cart mutation routes. `item.url_to_remove` in the cart drawer/
 *     cart page (and Add to Cart itself) are these; a prefetch would
 *     silently remove/change a cart line just from a hover.
 *   - /account/logout — a prefetch would log the shopper out without
 *     them clicking anything.
 *
 * Add to Cart and newsletter/subscribe forms are NOT links at all
 * (real <button type="submit"> inside a <form>, confirmed in
 * snippets/buy-buttons.liquid and sections/newsletter.liquid) — the
 * `a[href]` selector already can't match them, this exclusion list is
 * only for genuine anchor tags that happen to carry a side effect.
 *
 * Harmless by contrast (left prefetchable): facet "remove filter"
 * links (`url_to_remove`/`results_url` in snippets/facets.liquid) only
 * change a collection page's query string — no mutation, just a GET.
 *
 * Uses <link rel="prefetch">, not rel="prerender" or the Speculation
 * Rules API — prefetch only caches the response; it does not parse/
 * execute the page, so no scripts (GTM/Klaviyo pixels, etc.) run until
 * a real click/navigation happens. No double-counting risk.
 *
 * Opt-out escape hatch for any future case this list misses: add
 * data-no-prefetch to any <a> to exclude it individually.
 */
(function () {
  var EXCLUDED_PATHNAME_PREFIXES = [
    '/cart/add',
    '/cart/change',
    '/cart/update',
    '/cart/clear',
    '/account/logout'
  ];

  var HOVER_DELAY_MS = 65; // matches instant.page's default
  var prefetched = new Set();
  var hoverTimers = new WeakMap();

  function isEligible(url, el) {
    if (!url) return false;
    if (el && el.closest('[data-no-prefetch]')) return false;
    if (el && (el.hasAttribute('download') || el.target === '_blank')) return false;
    try {
      var u = new URL(url, window.location.href);
      if (u.origin !== window.location.origin) return false;
      if (u.pathname === window.location.pathname && u.search === window.location.search) return false;
      for (var i = 0; i < EXCLUDED_PATHNAME_PREFIXES.length; i++) {
        if (u.pathname.indexOf(EXCLUDED_PATHNAME_PREFIXES[i]) === 0) return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function prefetch(url) {
    if (prefetched.has(url)) return;
    prefetched.add(url);
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  }

  function saveDataOn() {
    return !!(navigator.connection && navigator.connection.saveData);
  }

  // Public hook for non-anchor hover targets (e.g. the swatch buttons in
  // native-collection-swatches.js) that have a destination URL but no
  // real <a href> for the global mouseover listener below to match.
  window.NativePrefetch = {
    request: function (url) {
      if (saveDataOn()) return;
      if (!isEligible(url, null)) return;
      prefetch(url);
    }
  };

  if (saveDataOn()) return;

  document.addEventListener('mouseover', function (event) {
    var el = event.target.closest('a[href]');
    if (!el || hoverTimers.has(el)) return;
    var url = el.href;
    if (!isEligible(url, el)) return;

    var timer = setTimeout(function () {
      prefetch(url);
    }, HOVER_DELAY_MS);
    hoverTimers.set(el, timer);
  });

  document.addEventListener('mouseout', function (event) {
    var el = event.target.closest('a[href]');
    if (!el) return;
    var timer = hoverTimers.get(el);
    if (timer) {
      clearTimeout(timer);
      hoverTimers.delete(el);
    }
  });

  // Touch devices have no hover — fire on touchstart instead, same as
  // instant.page's mobile fallback.
  document.addEventListener('touchstart', function (event) {
    var el = event.target.closest('a[href]');
    if (!el) return;
    var url = el.href;
    if (isEligible(url, el)) prefetch(url);
  }, { passive: true });
})();
